<template>
  <v-container fluid class="handbook-dashboard-container">
    <!-- Manager overlay for add buttons -->
    <div class="add-buttons-overlay" v-if="isManager">
      <v-btn small color="primary" @click="createCategory">
        <span class="button-text">Add Category</span>
      </v-btn>
      <v-btn small color="secondary" @click="createArticle">
        <span class="button-text">Add Article</span>
      </v-btn>
    </div>

    <v-row>
      <!-- LEFT: Category Filter -->
      <v-col cols="12" md="3" class="mt-custom">
        <v-card class="filter-card" outlined>
          <v-card-title>Categories</v-card-title>
          <v-card-text>
            <v-list dense>
              <v-list-item
                v-for="cat in sortedCategories"
                :key="cat.id"
                dense
                :class="['category-item', { 'active-category': selectedCategory === cat.id }]"
                @click="selectCategory(cat.id)"
                style="cursor: pointer;"
              >
                <v-list-item-title class="d-flex align-center" style="padding-right: 0;">
                  {{ cat.name }}
                  <div v-if="isManager" class="ml-auto" style="display: flex; align-items: center;">
                    <SvgIcon
                      :path="mdiPencil"
                      size="24"
                      class="text-warning ml-2"
                      style="cursor: pointer;"
                      @click.stop="editCategory(cat.id)"
                    />
                    <SvgIcon
                      :path="mdiDelete"
                      size="24"
                      class="text-error ml-1"
                      style="cursor: pointer;"
                      @click.stop="deleteCategory(cat.id)"
                    />
                  </div>
                </v-list-item-title>
                <v-list-item-subtitle v-if="cat.description">
                  {{ cat.description }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- RIGHT: Search + Article Grid -->
      <v-col cols="12" md="9">
        <v-row align="center" class="mb-4">
          <v-col cols="12" md="8">
            <v-text-field v-model="searchQuery" label="Search articles..." clearable>
              <template #append>
                <SvgIcon :path="mdiMagnify" />
              </template>
            </v-text-field>
          </v-col>
        </v-row>

        <v-row v-if="loadingArticles" class="articles-grid">
          <v-col cols="12" sm="6" md="4" v-for="n in 6" :key="n">
            <v-skeleton-loader type="card" class="article-card" />
          </v-col>
        </v-row>

        <v-row class="articles-grid" v-else>
          <v-col cols="12" sm="6" md="4" v-for="article in filteredArticles"
                :key="article.id"
                @mouseenter="prefetchSection(article)"
                @click="cardClicked($event, article)"
          >
            <v-card class="article-card" elevation="2" style="cursor: pointer; position: relative;">
              <v-tooltip>
                <template #activator="{ props }">
                  <SvgIcon
                    v-bind="props"
                    v-if="article.ack_status"
                    :path="iconForStatus(article.ack_status).path"
                    size="20"
                    :class="['status-icon', iconForStatus(article.ack_status).css]"
                  />
                </template>
                <span>{{ iconForStatus(article.ack_status).tooltip }}</span>
              </v-tooltip>
              <v-card-title class="headline d-flex align-center" :title="article.title">
                <span class="title-text">{{ article.title }}</span>
              </v-card-title>
              <v-card-text>
                <!-- Show description if present, else card-text is empty -->
                <div
                  v-if="article.description"
                  v-text="article.description"
                  v-shave="100"
                  class="article-preview"
                ></div>
              </v-card-text>

              <!-- Manager inline actions -->
              <v-card-actions v-if="isManager" class="manager-actions">
                <v-btn icon @click.stop="editArticle(article.id)">
                  <SvgIcon :path="mdiPencil" size="24" class="text-warning" />
                </v-btn>
                <v-btn icon class="delete-btn" @click.stop="deleteArticle(article.id)">
                  <SvgIcon :path="mdiDelete" size="24" class="text-error" />
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>

          <v-col cols="12" v-if="!loadingArticles && filteredArticles.length === 0">
            <v-alert type="info">
              {{ emptyStateMessage }}
            </v-alert>
          </v-col>
        </v-row>
        <v-row justify="center" v-if="nextPageUrl && filteredArticles.length > 0" class="my-4">
          <v-btn
            :loading="loadingMore"
            color="primary"
            @click="loadMore"
          >
            Load more
          </v-btn>
        </v-row>
      </v-col>
    </v-row>

    <!-- The CHAIN-based modal -->
    <HandbookArticleModal
      :showDialog="chain.length > 0"
      :article="currentArticle"
      :chainLength="chain.length"
      :prevTitle="prevTitle"
      :isManager="isManager"
      @edit="editArticle"
      @delete="deleteArticle"
      @mention-link-clicked="mentionLinkClicked"
      @pop-one="popOne"
      @close-all="closeAll"
      @acknowledged="onArticleAcked"
      @prefetch-chain-link="prefetchSectionById"
    />
  </v-container>
</template>

<script>
import api from "@/plugins/axios";
import shave from "shave";
import { watch, nextTick } from "vue";
import debounce from 'lodash/debounce';
import HandbookArticleModal from "@/views/HandbookArticleModal.vue";
import SvgIcon from "@/components/SvgIcon.vue";
import { mdiPencil, mdiDelete, mdiMagnify } from "@mdi/js";
import { iconForStatus } from "@/utils/ackStatus";
import { useUserStore } from '@/stores/user';

// Idle-time prefetch of the TipTap ArticleContent chunk so first open is snappy.
// This downloads the viewer code in the background *after* the dashboard is ready.
const __idle = window.requestIdleCallback || (cb => setTimeout(cb, 300));
__idle(() => {
  import('@/tiptap/components/ArticleContent.vue').catch(() => {
    /* ignore: user might navigate away quickly */
  });
});


// Simple SWR cache for prefetched sections
const sectionCache = new Map();
const MAX_PREFETCH = 30;

// Batch/cleanup shave work to avoid layout thrash
const shaveJobs = new WeakMap();
function runShave(el, value) {
  if (shaveJobs.has(el)) {
    cancelAnimationFrame(shaveJobs.get(el));
    shaveJobs.delete(el);
  }
  const id = requestAnimationFrame(() => {
    try {
      shave(el, value || 100, { character: "…" });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[shave] failed to trim text', err);
      }
    }
    shaveJobs.delete(el);
  });
  shaveJobs.set(el, id);
}

export default {
  name: "HandbookDashboard",
  directives: {
    shave: {
      mounted(el, binding) {
        nextTick(() => runShave(el, binding.value));
      },
      updated(el, binding) {
        nextTick(() => runShave(el, binding.value));
      },
      unmounted(el) {
        if (shaveJobs.has(el)) {
          cancelAnimationFrame(shaveJobs.get(el));
          shaveJobs.delete(el);
        }
      },
    },
  },
  components: { HandbookArticleModal, SvgIcon },
  data() {
    return {
      userStore: null,
      categories: [],
      articles: [],
      searchQuery: "",
      selectedCategory: null,
      chain: [],
      mdiPencil,
      mdiDelete,
      mdiMagnify,
      loadingArticles: true,
      page: 1,
      nextPageUrl: null,
      loadingMore: false,
      nextPagePrefetch: null,
      nextPagePrefetchUrl: null,
      nextPagePrefetching: false,
    };
  },
  computed: {
    currentArticle() {
      return this.chain.length
        ? this.chain[this.chain.length - 1]
        : {};
    },
    prevTitle() {
      return this.chain.length > 1
        ? this.chain[this.chain.length - 2].title
        : "";
    },
    filteredArticles() {
      // If no category chosen and no search, show nothing on the dashboard
      if (this.selectedCategory === null && !this.searchQuery) {
        return [];
      }

      // When searching, the backend already filtered & paginated results via ?search=
      if (this.searchQuery) {
        return this.articles.slice().sort((a, b) => a.title.localeCompare(b.title));
      }

      // Otherwise filter by selected category only (client-side) and sort
      let list = this.articles;
      if (this.selectedCategory !== null) {
        list = list.filter((a) => +a.category === +this.selectedCategory);
      }
      return list.slice().sort((a, b) => a.title.localeCompare(b.title));
    },
    emptyStateMessage() {
      if (this.selectedCategory === null && !this.searchQuery) {
        return 'Choose a category or search for content.';
      }
      if (this.searchQuery && (!this.filteredArticles || this.filteredArticles.length === 0)) {
        return 'No results for your search.';
      }
      if (this.selectedCategory !== null && !this.searchQuery) {
        return 'No articles in this category yet.';
      }
      // Fallback
      return 'No articles found.';
    },
    user() {
      return this.userStore?.user || null;
    },
    isManager() {
      const role = this.userStore?.user?.role;
      return role === 'manager' || role === 'supermanager';
    },
    sortedCategories() {
      return this.categories
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  methods: {
    selectCategory(catId) {
      this.selectedCategory = catId;
      try {
        sessionStorage.setItem('handbook.returnCategory', String(catId));
      } catch (e) { /* ignore storage errors (private mode, quota, etc.) */ }
      // Refetch so backend paginates within this category
      this.fetchData();
    },
    prefetchNextPageIdle() {
      if (!this.nextPageUrl || this.nextPagePrefetching) return;
      if (this.nextPagePrefetch && this.nextPagePrefetchUrl === this.nextPageUrl) return;
      const idle = window.requestIdleCallback || function(cb){ return setTimeout(() => cb({ didTimeout:false, timeRemaining: () => 0 }), 200); };
      this.nextPagePrefetching = true;
      const urlToPrefetch = this.nextPageUrl;
      idle(async () => {
        try {
          const res = await api.get(urlToPrefetch);
          // Only store if we're still on the same next URL
          if (this.nextPageUrl === urlToPrefetch) {
            this.nextPagePrefetch = res.data;
            this.nextPagePrefetchUrl = urlToPrefetch;
          }
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') console.debug('prefetch next page failed', err);
        } finally {
          this.nextPagePrefetching = false;
        }
      });
    },
    /**
     * Prefetch an article on hover and cache it.
     */
    async prefetchSection(article) {
      if (sectionCache.has(article.id)) return;
      try {
        const { data } = await api.get(`/api/handbook-sections/${article.id}/`);
        sectionCache.set(article.id, data);
        // cap cache to most recent MAX_PREFETCH entries
        if (sectionCache.size > MAX_PREFETCH) {
          const oldestKey = sectionCache.keys().next().value;
          sectionCache.delete(oldestKey);
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.debug('prefetch failed', e);
      }
    },
    iconForStatus,
    async fetchData() {
      this.loadingArticles = true;
      try {
        // 1) Always fetch categories first
        const catsRes = await api.get('/api/handbook-categories/');
        this.categories = catsRes.data;

        // 2) If no category chosen (and not searching), default to the first category
        if (!this.searchQuery && (this.selectedCategory === null || this.selectedCategory === undefined)) {
          const first = this.categories && this.categories.length ? this.categories[0] : null;
          if (first && first.id !== undefined && first.id !== null) {
            this.selectedCategory = Number(first.id);
            try { sessionStorage.setItem('handbook.returnCategory', String(this.selectedCategory)); }
            catch (_) { /* ignore storage errors (private mode, quota, etc.) */ }
          }
        }

        // 3) Now fetch sections using the (possibly defaulted) category or search
        const base = '/api/handbook-sections/?page=1';
        let url = base;
        if (this.searchQuery) {
          url += `&search=${encodeURIComponent(this.searchQuery)}`;
        } else if (this.selectedCategory !== null && this.selectedCategory !== undefined) {
          url += `&category=${encodeURIComponent(this.selectedCategory)}`;
        }

        const secsRes = await api.get(url);
        this.articles = secsRes.data.results || [];

        // Normalize next page URL relative to origin
        const rawNext = secsRes.data.next;
        this.nextPageUrl = null;
        if (rawNext) {
          const u = new URL(rawNext, window.location.origin);
          this.nextPageUrl = u.pathname + u.search;
        }

        this.page = 1;
        // reset any previous prefetch cache and prefetch next if available
        this.nextPagePrefetch = null;
        this.nextPagePrefetchUrl = null;
        this.nextPagePrefetching = false;
        this.prefetchNextPageIdle();
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingArticles = false;
      }
    },
    createCategory() {
      this.$router.push("/handbook/category/create");
    },
    createArticle() {
      this.$router.push("/handbook/article/create");
    },
    editCategory(id) {
      this.$router.push({ name: 'CategoryEdit', params: { id } });
    },
    deleteCategory(id) {
      if (!confirm("Are you sure you want to delete this category?")) return;
      api
        .delete(`/api/handbook-categories/${id}/`)
        .then(() => {
          // remove from local categories list
          this.categories = this.categories.filter(c => c.id !== id);
          if (this.selectedCategory === id) {
            this.selectedCategory = null;
          }
        })
        .catch(e => {
          const status = e.response?.status;
          const detail = e.response?.data?.detail;
          if (status === 400) {
            // ProtectedError mapped to 400: cannot delete non-empty category
            alert(detail || 'Cannot delete this category because it still has articles. Please reassign or delete articles first.');
          } else {
            console.error(e);
            alert('An error occurred while deleting the category.');
          }
        });
    },
    editArticle(id) {
      try {
        if (this.selectedCategory !== null && this.selectedCategory !== undefined) {
          sessionStorage.setItem('handbook.returnCategory', String(this.selectedCategory));
        } else {
          sessionStorage.removeItem('handbook.returnCategory');
        }
      } catch (e) {
        // ignore storage errors (private mode, etc.)
      }
      this.$router.push(`/handbook/article/${id}/edit`);
    },
    deleteArticle(id) {
      if (!confirm("Are you sure?")) return;
      api
        .delete(`/api/handbook-sections/${id}/`)
        .then(() => {
          this.articles = this.articles.filter((a) => a.id !== id);
          const idx = this.chain.findIndex((a) => a.id === id);
          if (idx !== -1) this.chain.splice(idx, 1);
        })
        .catch((e) => console.error(e));
    },
    async cardClicked(e, article) {
      // decide which ID to load
      const link = e.target.closest("a");
      const id = link
        ? Number(link.href.split("/").pop().split("?")[0])
        : article.id;
      this.chain.push({
        id,
        title: article.title,
        description: article.description,
        ack_status: article.ack_status,
        current_history_id: article.current_history_id,
      });
    },
    async mentionLinkClicked({ newId }) {
      const id = Number(newId);
      const found = this.articles.find(a => a.id === id);
      this.chain.push(found || { id, title: 'Article' });
    },
    popOne() {
      this.chain.pop();
    },
    closeAll() {
      this.chain = [];
    },
    async loadMore() {
      if (!this.nextPageUrl) return;
      this.loadingMore = true;
      try {
        let data = null;
        if (this.nextPagePrefetch && this.nextPagePrefetchUrl === this.nextPageUrl) {
          data = this.nextPagePrefetch;
        } else {
          const res = await api.get(this.nextPageUrl);
          data = res.data;
        }
        this.articles = this.articles.concat(data.results || []);
        const rawNext = data.next;
        this.nextPageUrl = null;
        if (rawNext) {
          const u = new URL(rawNext, window.location.origin);
          this.nextPageUrl = u.pathname + u.search;
        }
        // clear consumed prefetch
        this.nextPagePrefetch = null;
        this.nextPagePrefetchUrl = null;
        this.page += 1;
        // kick off prefetch of the new next page
        this.prefetchNextPageIdle();
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingMore = false;
      }
    },
    onArticleAcked(id, freshArticle = null) {
      // Find the article in the dashboard list
      const art = this.articles.find(a => a.id === id);
      if (art) {
        // Keep list tile in sync immediately with the canonical detail payload.
        if (freshArticle && typeof freshArticle === 'object') {
          art.ack_status = freshArticle.ack_status || 'read';
          art.last_read_id = freshArticle.last_read_id ?? freshArticle.last_read_history_id ?? art.last_read_id ?? null;
          art.last_read_history_id = freshArticle.last_read_history_id ?? art.last_read_history_id ?? null;
          art.current_history_id = freshArticle.current_history_id ?? art.current_history_id ?? null;
        } else {
          art.ack_status = 'read';
          art.last_read_id = art.current_history_id || null;
        }
      }
    },
    /**
     * Handler for modal internal-link prefetch events.
     */
    prefetchSectionById(id) {
      this.prefetchSection({ id });
    }
  },
  created() {
    this.userStore = useUserStore();
    // Ensure user store is hydrated like Home/Daily/Forum
    if (!this.userStore || !this.userStore.user) {
      try {
        const store = this.userStore || useUserStore();
        // Fire-and-forget; we don't need to block component creation
        store.fetchUser().catch(() => {});
        this.userStore = store;
      } catch (e) {
        // No-op: if Pinia store init fails, proceed without user; other pages will hydrate.
      }
    }
    try {
      const ret = sessionStorage.getItem('handbook.returnCategory');
      if (ret !== null && ret !== '') {
        this.selectedCategory = Number(ret);
      }
      sessionStorage.removeItem('handbook.returnCategory');
    } catch (e) {
      // ignore storage errors
    }
    this.fetchData();
    this.debouncedFetchData = debounce(this.fetchData, 300);
  },
  mounted() {
    setTimeout(() => window.dispatchEvent(new Event("resize")), 200);
    watch(
      () => this.user?.active_bar,
      (newVal, oldVal) => {
        // Only react when the bar actually changes after mount
        if (oldVal === undefined) return; // ignore initial run
        if (newVal !== oldVal) {
          // Clear any stale category selection when switching bars
          this.selectedCategory = null;
          try { sessionStorage.removeItem('handbook.returnCategory'); }
          catch (_) { /* ignore storage errors (private mode, quota, etc.) */ }
          this.fetchData();
        }
      },
      { immediate: false }
    );
    watch(
      () => this.searchQuery,
      (newQ, oldQ) => {
        if (newQ !== oldQ) this.debouncedFetchData();
      }
    );
    watch(
      () => this.selectedCategory,
      (n, o) => {
        if (n !== o) {
          this.fetchData();
        }
      }
    );
  },
};
</script>

<style scoped>
.handbook-dashboard-container {
  position: relative;
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  padding: 20px;
  min-height: 100vh;
}
.mt-custom {
  margin-top: 70px;
}

/* Category filter */
.filter-card {
  background-color: #f0f0f0 !important;
  border-radius: 8px;
  border: none !important;
}
.filter-card .v-card-title,
.filter-card .v-card-text,
.filter-card .v-list,
.filter-card .v-list-item {
  background-color: #f0f0f0 !important;
}
.category-item {
  background-color: #f0f0f0 !important;
}
.v-list-item-title,
.v-list-item-subtitle {
  color: #333 !important;
}
.active-category {
  background-color: #e0f7fa;
  border-left: 4px solid #00acc1;
  color: #007c91;
}

/* Articles grid */
.articles-grid {
  position: relative;
  top: -48px;
}
.article-card {
  display: flex;
  flex-direction: column;
  height: 180px;
  background: linear-gradient(135deg, #ffffff, #fefefe);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.1s ease;
}

.article-card:active {
  transform: scale(0.98);
}


.article-card .v-card-title {
  min-height: 50px;
  padding: 16px;
  overflow: hidden;
}
.article-card .title-text {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.article-card .v-card-text {
  flex: 1;
  padding: 16px;
  position: relative;
  overflow: hidden;
}
.article-preview {
  margin-top: -1.7rem;
  padding: 0.5rem 0.5rem 0.5rem 0;
  overflow: hidden;
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}
.article-preview ol,
.article-preview ul {
  margin: 0;
  padding-left: 1.5rem;
}

/* Manager inline actions */
.manager-actions {
  display: flex;
  justify-content: flex-start;
  gap: 0;
  padding: 0;
}
.manager-actions > * {
  margin: 0 !important;
}
.delete-btn {
  margin-left: -15px !important;
}

/* ← UPDATED overlay CSS: left‑pinned + ellipsize */
.add-buttons-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  gap: 8px;
  z-index: 10;
}
.add-buttons-overlay .button-text {
  display: inline-block;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.add-buttons-overlay .v-btn {
  font-size: 0.875rem;
}

/* shrink + force earlier ellipsis on iPad‑landscape & smaller */
@media (max-width: 1024px) {
  .add-buttons-overlay .v-btn {
    font-size: 0.75rem;
    padding: 4px 8px;
  }
  .add-buttons-overlay .button-text {
    max-width: 60px;
  }
}

/* Dialog styles */
.expanded-article-card {
  position: relative;
  border-radius: 8px;
}
.dialog-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
}
.expanded-article-content {
  padding: 16px;
  overflow-wrap: break-word;
}
.expanded-article-content ol,
.expanded-article-content ul {
  margin: 0;
  padding-left: 1.5rem;
}
.status-icon {
  position: absolute;
  top: 8px;
  right: 8px;
}
</style>
